import { supabase, isSupabaseConfigured } from '../lib/supabase';
import { DatabaseValidation, UUIDValidation } from '../utils/validation';

export interface ClientNote {
  id: string;
  customer_id: string;
  note_text: string;
  created_at: string;
  user_id?: string;
  user_name?: string;
  is_pinned?: boolean;
}

export class ClientNotesService {
  /**
   * Toggle the pinned status of a note
   */
  static async toggleNotePin(noteId: string, isPinned: boolean): Promise<void> {
    if (!isSupabaseConfigured()) {
      throw new Error('Supabase is not configured');
    }

    try {
      const { error } = await supabase
        .from('client_notes')
        .update({ is_pinned: isPinned })
        .eq('id', noteId);

      if (error) {
        const msg = error.message || '';
        const missingColumn =
          error.code === 'PGRST204' ||
          msg.toLowerCase().includes('is_pinned') ||
          msg.toLowerCase().includes('schema cache');
        if (missingColumn) {
          throw new Error(
            'Pinning is not available on this database yet. Run the client_notes is_pinned migration, then try again.'
          );
        }
        console.error('Error toggling note pin:', error);
        throw error;
      }
    } catch (error) {
      console.error('Error in toggleNotePin:', error);
      throw error;
    }
  }

  /**
   * Add a new client note
   */
  static async addClientNote(customerId: string, noteText: string, userId?: string, userName?: string): Promise<void> {
    if (!isSupabaseConfigured()) {
      console.warn('ClientNotesService.addClientNote: Supabase not configured, skipping note');
      return;
    }
    try {
      const validCustomerId = DatabaseValidation.validateCustomerId(customerId);
      const trimmed = (noteText || '').trim();
      if (!trimmed) {
        console.warn('ClientNotesService.addClientNote: empty note text, skipping');
        return;
      }
      const resolvedName = userName?.trim() || 'System';
      // Many remote DBs only have id, customer_id, note_text, created_at — no user_id / user_name / is_pinned.
      // Put the actor in the text so the timeline still shows who did it after reload.
      const authorPrefix =
        resolvedName && resolvedName !== 'System' ? `[${resolvedName}] ` : '';
      const noteBody = `${authorPrefix}${trimmed}`;

      const base = {
        id: UUIDValidation.generateUUID(),
        customer_id: validCustomerId,
        created_at: new Date().toISOString()
      };
      // Schema drift: some DBs require `note` NOT NULL, others only `note_text` (see silver_cell migration).
      const attempts = [
        { ...base, note: noteBody, note_text: noteBody },
        { ...base, note_text: noteBody },
        { ...base, note: noteBody }
      ];

      for (let i = 0; i < attempts.length; i++) {
        const { error } = await supabase.from('client_notes').insert(attempts[i]);
        if (!error) return;
        const msg = error.message || '';
        const retryable =
          error.code === 'PGRST204' ||
          (error.code === '23502' && msg.includes('column "note"'));
        if (!retryable) {
          console.error('Error adding client note:', error);
          throw error;
        }
        if (i === attempts.length - 1) {
          console.error('Error adding client note:', error);
          throw error;
        }
      }
    } catch (error) {
      console.error('Error in addClientNote:', {
        error,
        customerId,
        customerIdType: typeof customerId,
        userId,
        userIdType: typeof userId
      });
      throw error;
    }
  }

  /**
   * Get all notes for a customer
   */
  static async getClientNotes(customerId: string): Promise<ClientNote[]> {
    try {
      // Return empty array if Supabase is not configured
      if (!isSupabaseConfigured()) {
        return [];
      }

      const { data, error } = await supabase
        .from('client_notes')
        .select('*')
        .eq('customer_id', customerId)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching client notes:', error);
        throw error;
      }

      return (data || []).map((row: Record<string, unknown>) => {
        const text = row.note_text ?? row.note ?? '';
        return { ...row, note_text: String(text) } as ClientNote;
      });
    } catch (error) {
      console.error('Error in getClientNotes:', error);
      throw error;
    }
  }

  /**
   * Helper function to log booking creation
   */
  static async logBookingCreated(customerId: string, bookingDetails: {
    courseName: string;
    courseDate: string;
    contactName?: string;
    instructorName?: string;
    location?: string;
  }, userId?: string, userName?: string): Promise<void> {
    const noteText = `Booking created: ${bookingDetails.courseName} on ${new Date(bookingDetails.courseDate).toLocaleDateString('en-GB')}` +
      (bookingDetails.contactName ? ` | Contact: ${bookingDetails.contactName}` : '') +
      (bookingDetails.instructorName ? ` | Instructor: ${bookingDetails.instructorName}` : '') +
      (bookingDetails.location ? ` | Location: ${bookingDetails.location}` : '');

    await this.addClientNote(customerId, noteText, userId, userName);
  }

  /**
   * Helper function to log booking updates with detailed change tracking
   */
  static async logBookingUpdated(customerId: string, bookingDetails: {
    courseName: string;
    courseDate: string;
    changes?: string;
    status?: string;
    previousStatus?: string;
    specificChanges?: string[];
    detailedChanges?: Array<{field: string, oldValue: any, newValue: any}>;
  }, userId?: string, userName?: string): Promise<void> {
    let noteText = `Booking updated: ${bookingDetails.courseName} on ${new Date(bookingDetails.courseDate).toLocaleDateString('en-GB')}`;
    
    // Format values for display
    const formatValue = (value: any): string => {
      if (value === null || value === undefined || value === '') return '(empty)';
      if (typeof value === 'boolean') return value ? 'Yes' : 'No';
      if (typeof value === 'number') return value.toString();
      return String(value);
    };

    // If detailed changes are provided, show them with old/new values
    if (bookingDetails.detailedChanges && bookingDetails.detailedChanges.length > 0) {
      const changeDescriptions = bookingDetails.detailedChanges.map(change => {
        const fieldLabels: Record<string, string> = {
          'status': 'Status',
          'courseName': 'Course Name',
          'courseDate': 'Course Date',
          'startTime': 'Start Time',
          'endTime': 'End Time',
          'totalPrice': 'Total Price',
          'instructorPrice': 'Instructor Price',
          'instructorPO': 'Instructor PO',
          'clientPO': 'Client PO',
          'attendeeCount': 'Attendee Count',
          'notes': 'Notes',
          'instructorId': 'Instructor',
          'contactId': 'Contact',
          'locationId': 'Location',
          'bookingType': 'Booking Type',
          'additionalDates': 'Additional Dates',
          'additionalDateTimes': 'Additional Date Times'
        };

        const fieldLabel = fieldLabels[change.field] || change.field;
        const oldVal = formatValue(change.oldValue);
        const newVal = formatValue(change.newValue);

        // Special formatting for certain fields
        if (change.field === 'courseDate') {
          const oldDate = change.oldValue ? new Date(change.oldValue).toLocaleDateString('en-GB') : '(empty)';
          const newDate = change.newValue ? new Date(change.newValue).toLocaleDateString('en-GB') : '(empty)';
          return `${fieldLabel}: ${oldDate} → ${newDate}`;
        } else if (change.field === 'status') {
          const statusLabels: Record<string, string> = {
            'scheduled': 'Scheduled',
            'confirmed': 'Confirmed',
            'reminder_email_sent': 'Reminder Email Sent',
            'reminder_email_2_sent': 'Reminder Email 2 Sent',
            'certs_produced': 'Certificates Produced',
            'job_paid': 'Job Paid',
            'completed': 'Completed',
            'cancelled': 'Cancelled'
          };
          const oldStatus = statusLabels[change.oldValue] || change.oldValue || '(empty)';
          const newStatus = statusLabels[change.newValue] || change.newValue || '(empty)';
          return `${fieldLabel}: ${oldStatus} → ${newStatus}`;
        } else if (change.field === 'totalPrice' || change.field === 'instructorPrice') {
          const oldPrice = change.oldValue ? `€${Number(change.oldValue).toFixed(2)}` : '(empty)';
          const newPrice = change.newValue ? `€${Number(change.newValue).toFixed(2)}` : '(empty)';
          return `${fieldLabel}: ${oldPrice} → ${newPrice}`;
        } else if (change.field === 'instructorId' || change.field === 'contactId' || change.field === 'locationId') {
          // For IDs, we might want to show names instead - but for now show IDs
          return `${fieldLabel}: ${oldVal} → ${newVal}`;
        } else {
          return `${fieldLabel}: "${oldVal}" → "${newVal}"`;
        }
      });

      noteText += `\n\nChanges:\n${changeDescriptions.map(c => `• ${c}`).join('\n')}`;
    } else if (bookingDetails.specificChanges && bookingDetails.specificChanges.length > 0) {
      // Fallback to simple change list if detailed changes not provided
      noteText += ` | Changes: ${bookingDetails.specificChanges.join(', ')}`;
    } else if (bookingDetails.changes) {
      noteText += ` | Changes: ${bookingDetails.changes}`;
    } else if (bookingDetails.status && bookingDetails.previousStatus && bookingDetails.status !== bookingDetails.previousStatus) {
      // Legacy status change logging
      const statusLabels: Record<string, string> = {
        'scheduled': 'Scheduled',
        'confirmed': 'Confirmed',
        'reminder_email_sent': 'Reminder Email Sent',
        'reminder_email_2_sent': 'Reminder Email 2 Sent',
        'certs_produced': 'Certificates Produced',
        'job_paid': 'Job Paid',
        'completed': 'Completed',
        'cancelled': 'Cancelled'
      };
      
      noteText += ` | Status changed from ${statusLabels[bookingDetails.previousStatus] || bookingDetails.previousStatus} to ${statusLabels[bookingDetails.status] || bookingDetails.status}`;
    }

    await this.addClientNote(customerId, noteText, userId, userName);
  }

  /**
   * Helper function to log email sent
   */
  static async logEmailSent(customerId: string, emailDetails: {
    type: 'booking_confirmation' | 'instructor_assignment' | 'reminder' | 'review_request';
    recipient: string;
    courseName?: string;
    courseDate?: string;
    additionalInfo?: string;
  }, userId?: string, userName?: string): Promise<void> {
    try {
      // Validate customer ID before proceeding
      if (!UUIDValidation.isValidUUID(customerId)) {
        console.error('Invalid customer ID for email logging:', {
          customerId,
          type: typeof customerId,
          emailDetails
        });
        throw new Error(`Invalid customer ID: expected UUID, got "${customerId}"`);
      }

      const emailTypeLabels = {
        booking_confirmation: 'Booking Confirmation',
        instructor_assignment: 'Instructor Assignment',
        reminder: 'Certificate Reminder',
        review_request: 'Review Request'
      };

      const noteText = `${emailTypeLabels[emailDetails.type]} email sent to ${emailDetails.recipient}` +
        (emailDetails.courseName ? ` | Course: ${emailDetails.courseName}` : '') +
        (emailDetails.courseDate ? ` | Date: ${new Date(emailDetails.courseDate).toLocaleDateString('en-GB')}` : '') +
        (emailDetails.additionalInfo ? ` | ${emailDetails.additionalInfo}` : '');

      await this.addClientNote(customerId, noteText, userId, userName);
    } catch (error) {
      console.error('Error logging email sent:', {
        error,
        customerId,
        customerIdType: typeof customerId,
        emailDetails
      });
      throw error;
    }
  }

  /**
   * Log enrolling a CRM client onto a scheduled course session (course_sessions / course_session_attendees).
   */
  static async logCourseEnrollment(
    customerId: string,
    details: {
      courseName: string;
      datesSummary: string;
      paymentLabel: string;
    },
    userId?: string,
    userName?: string
  ): Promise<void> {
    const noteText =
      `Added to course session: ${details.courseName}` +
      (details.datesSummary ? ` | Dates: ${details.datesSummary}` : '') +
      ` | ${details.paymentLabel}`;
    await this.addClientNote(customerId, noteText, userId, userName);
  }

  /**
   * Logs a training/course-related change on the individual client's CRM record only (not on the session row).
   */
  static async logClientTrainingUpdate(
    customerId: string,
    details: { courseName: string; datesSummary?: string | null },
    message: string,
    userId?: string,
    userName?: string
  ): Promise<void> {
    const msg = (message || '').trim();
    if (!msg) return;
    const ds = (details.datesSummary || '').trim();
    const ctx = ds ? `${details.courseName} (${ds})` : details.courseName;
    await this.addClientNote(customerId, `${ctx}: ${msg}`, userId, userName);
  }

  /**
   * Helper function to log booking status changes
   */
  static async logBookingStatusChange(customerId: string, bookingDetails: {
    courseName: string;
    courseDate: string;
    status: string;
    previousStatus?: string;
    action?: string;
  }, userId?: string, userName?: string): Promise<void> {
    const statusLabels: Record<string, string> = {
      'scheduled': 'Scheduled',
      'confirmed': 'Confirmed',
      'reminder_email_sent': 'Reminder Email Sent',
      'reminder_email_2_sent': 'Reminder Email 2 Sent',
      'certs_produced': 'Certificates Produced',
      'job_paid': 'Job Paid',
      'completed': 'Completed',
      'cancelled': 'Cancelled'
    };

    let noteText = `Booking: ${bookingDetails.courseName} on ${new Date(bookingDetails.courseDate).toLocaleDateString('en-GB')}`;
    
    if (bookingDetails.action) {
      noteText += ` | ${bookingDetails.action}`;
    } else if (bookingDetails.previousStatus && bookingDetails.status !== bookingDetails.previousStatus) {
      noteText += ` | Status changed from ${statusLabels[bookingDetails.previousStatus] || bookingDetails.previousStatus} to ${statusLabels[bookingDetails.status] || bookingDetails.status}`;
    } else {
      noteText += ` | Status: ${statusLabels[bookingDetails.status] || bookingDetails.status}`;
    }

    await this.addClientNote(customerId, noteText, userId, userName);
  }

  /**
   * Helper function to log certificate generation
   */
  static async logCertificateGenerated(customerId: string, certificateDetails: {
    attendeeName: string;
    courseName: string;
    courseDate: string;
    count?: number;
  }, userId?: string, userName?: string): Promise<void> {
    const noteText = certificateDetails.count && certificateDetails.count > 1
      ? `${certificateDetails.count} certificates generated for ${certificateDetails.courseName} (${new Date(certificateDetails.courseDate).toLocaleDateString('en-GB')})`
      : `Certificate generated for ${certificateDetails.attendeeName} | Course: ${certificateDetails.courseName} | Date: ${new Date(certificateDetails.courseDate).toLocaleDateString('en-GB')}`;

    await this.addClientNote(customerId, noteText, userId, userName);
  }

  /**
   * Helper function to log payment status changes
   */
  static async logPaymentStatusChange(customerId: string, paymentDetails: {
    action: 'marked_paid' | 'reverted_payment';
    certificateCount: number;
    amount?: number;
  }, userId?: string, userName?: string): Promise<void> {
    const noteText = paymentDetails.action === 'marked_paid' 
      ? `Payment marked as completed for ${paymentDetails.certificateCount} certificate${paymentDetails.certificateCount !== 1 ? 's' : ''}` +
        (paymentDetails.amount ? ` | Amount: €${paymentDetails.amount.toFixed(2)}` : '')
      : `Payment status reverted | ${paymentDetails.certificateCount} certificate${paymentDetails.certificateCount !== 1 ? 's' : ''} unmarked`;

    await this.addClientNote(customerId, noteText, userId, userName);
  }

  /**
   * Helper function to log reminder email sent for specific booking
   */
  static async logBookingReminderEmail(customerId: string, bookingDetails: {
    courseName: string;
    courseDate: string;
    recipient: string;
    reminderType: 'first' | 'second';
  }, userId?: string, userName?: string): Promise<void> {
    const noteText = `Booking: ${bookingDetails.courseName} on ${new Date(bookingDetails.courseDate).toLocaleDateString('en-GB')} | ${bookingDetails.reminderType === 'first' ? 'First' : 'Second'} reminder email sent to ${bookingDetails.recipient}`;

    await this.addClientNote(customerId, noteText, userId, userName);
  }
}